import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { get } from 'lodash'
import AdminCommonFormLayout from '../../layout/AdminCommonFormLayout'
import { showSuccess, showError } from 'actions/Error'
import { adminBeneficiaryStates, adminContactRoles } from 'admin/actions/admin_dropdown_options'
import AdminCommonCompany from '../../companies/AdminCommonCompany'
import { handleSubmitResult } from 'actions/form'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@material-ui/core'
import {
  adminBeneficiaryVettingList,
  hasPrimaryContactForVetting,
  hasAtLeastOneAddressForVetting
} from '../actions/admin_beneficiary_vetting'
import { adminBeneficiaryVettingDemographicList } from '../actions/admin_beneficiary_vetting_demographic'
import { adminContactListVetting } from 'admin/components/contacts/actions/admin_contact'
import { adminAddressListVetting } from 'admin/components/addresses/actions/admin_address'
import {
  vettingToFormInitialValues,
  getDefaultInitialValues,
  formValuesToVettingPayload,
  vettingFormHasValidationErrors
} from './beneficiaryVettingFormUtils'
import { validationVettingSchema } from './AdminBeneficiaryVettingDetailsFormExtended'

export default function AdminBeneficiaryVetting() {
  const history = useHistory()
  const dispatch = useDispatch()
  const { beneficiary_id } = useParams()
  const [is_approving, set_is_approving] = useState(false)
  const [has_resumed, set_has_resumed] = useState(false)
  const [approvalDialogOpen, set_approvalDialogOpen] = useState(false)

  const vetting = useSelector(() => adminBeneficiaryVettingList.getObject(beneficiary_id))
  const vettingDemographics = useSelector(() => adminBeneficiaryVettingDemographicList.getVisibleObjects())
  const is_loading = useSelector(() => adminBeneficiaryVettingList.isLoading()) || (beneficiary_id && !get(vetting, 'id'))
  const is_busy = useSelector(() => adminBeneficiaryVettingList.getIsSavingObject())
  const current_status = get(vetting, 'status')
  const has_primary_contact = useSelector(() => hasPrimaryContactForVetting(beneficiary_id))
  const has_at_least_one_address = useSelector(() => hasAtLeastOneAddressForVetting(beneficiary_id))

  const title = beneficiary_id && vetting
    ? (get(vetting, 'company_legal_name', '') || 'Beneficiary Vetting')
    : 'New Beneficiary Vetting'

  const initial_values = beneficiary_id
    ? vettingToFormInitialValues(vetting, vettingDemographics)
    : getDefaultInitialValues()

  useEffect(() => {
    if (beneficiary_id) {
      dispatch(adminBeneficiaryVettingList.invalidateObject(beneficiary_id))
      dispatch(adminBeneficiaryVettingList.ensureObjectLoaded(beneficiary_id))
      dispatch(adminBeneficiaryVettingDemographicList.updateListFilter({ beneficiary_vetting: beneficiary_id }))
      dispatch(adminBeneficiaryVettingDemographicList.fetchListIfNeeded())
      dispatch(adminContactListVetting.updateListFilter({ beneficiary_vetting: beneficiary_id }))
      dispatch(adminContactListVetting.fetchListIfNeeded())
      dispatch(adminAddressListVetting.updateListFilter({ beneficiary_vetting: beneficiary_id }))
      dispatch(adminAddressListVetting.fetchListIfNeeded())
    }
    dispatch(adminBeneficiaryStates.fetchListIfNeeded())
    dispatch(adminContactRoles.fetchListIfNeeded())
  }, [dispatch, beneficiary_id])

  useEffect(() => {
    if (current_status === 'declined') {
      set_has_resumed(false)
    }
  }, [current_status])

  const onSave = (values, formik_funcs) => {
    const payload = formValuesToVettingPayload(values)
    const on_ok = function (json) {
      dispatch(adminBeneficiaryVettingList.invalidateList())
      if (beneficiary_id) {
        dispatch(adminBeneficiaryVettingList.invalidateObject(beneficiary_id))
        dispatch(adminBeneficiaryVettingList.ensureObjectLoaded(beneficiary_id))
      }
      showSuccess('Saved', 'Beneficiary vetting saved')
      if (!beneficiary_id) {
        history.push(`/admin/beneficiary_vetting/${json.id}`)
      }
    }
    if (beneficiary_id) {
      if (!payload.id) payload.id = get(vetting, 'id') || beneficiary_id
      return dispatch(adminBeneficiaryVettingList.saveObject(payload))
        .then((res) => handleSubmitResult({ res, formik_funcs, on_ok }))
    }
    return dispatch(adminBeneficiaryVettingList.saveNewObject(payload))
      .then((res) => handleSubmitResult({ res, formik_funcs, on_ok }))
  }

  const openApprovalDialog = () => {
    if (beneficiary_id && !has_primary_contact) {
      showError('Cannot approve', 'Add at least one primary contact before approving.')
      return
    }
    if (beneficiary_id && !has_at_least_one_address) {
      showError('Cannot approve', 'Add at least one address before approving.')
      return
    }
    set_approvalDialogOpen(true)
  }

  const closeApprovalDialog = () => set_approvalDialogOpen(false)

  const confirmApproval = async () => {
    closeApprovalDialog()
    set_is_approving(true)
    try {
      await dispatch(adminBeneficiaryVettingList.approveVetting({ beneficiary_vetting_id: beneficiary_id }))
      showSuccess('Beneficiary Approved', 'This beneficiary has been approved successfully.')
      dispatch(adminBeneficiaryVettingList.invalidateObject(beneficiary_id))
      dispatch(adminBeneficiaryVettingList.ensureObjectLoaded(beneficiary_id))
    } catch (err) {
      showError('Approval Failed', err?.message || 'An error occurred while approving the beneficiary.')
    } finally {
      set_is_approving(false)
    }
  }

  const resumeApprovalProcess = (formik_props) => {
    return () => {
      if (!beneficiary_id) return
      const resumedVetting = { ...vetting, status: 'vetting_in_progress' }
      formik_props.setFieldValue('status', 'vetting_in_progress')
      dispatch(adminBeneficiaryVettingList.updateObject(resumedVetting))
      const resumedValues = vettingToFormInitialValues(resumedVetting, vettingDemographics)
      formik_props.resetForm({ values: resumedValues })
      set_has_resumed(true)
      showSuccess('Resumed', 'Status set to In progress. Click Save to persist.')
    }
  }

  return (
    <AdminCommonFormLayout
      breadcrumbs={[
        { name: 'admin_home' },
        { name: 'beneficiary_vetting', label: 'Beneficiary Vetting', url: '/admin/beneficiaries_vetting' },
        {
          name: 'beneficiary_vetting_beneficiary',
          label: title,
          url: `/admin/beneficiary_vetting/${beneficiary_id || ''}`
        }
      ]}
      is_busy={is_busy}
      is_loading={is_loading}
      title={title}
      initial_values={initial_values}
      validationSchema={validationVettingSchema}
      validateOnMount={true}
      onSave={onSave}
    >
      {({ formik_props }) => (
        <>
          <AdminCommonCompany
            company_id={vetting.company_id}
            formik_props={formik_props}
            form_variation="beneficiary_vetting"
            is_loading={is_loading}
            state_history={null}
          />
          <div style={{ position: 'fixed', bottom: 15, right: 200, zIndex: 100 }}>
            {/* 
            note: resume logic to be implemented during the revetting process
            
            {current_status === 'declined' && !has_resumed ? (
              <Button
                variant="outlined"
                size="large"
                color="primary"
                disabled={!beneficiary_id || formik_props.isSubmitting || is_busy}
                onClick={resumeApprovalProcess(formik_props)}
              >
                Resume Approval Process
              </Button>
            ) : ( */}
              <Button
                variant="outlined"
                size="large"
                color="primary"
                disabled={
                  !beneficiary_id ||
                  current_status === 'approved' ||
                  current_status === 'declined' ||
                  (vettingFormHasValidationErrors(formik_props.errors) && formik_props.submitCount > 0) ||
                  formik_props.isSubmitting ||
                  is_approving ||
                  is_busy
                }
                onClick={openApprovalDialog}
              >
                Approve Beneficiary
              </Button>
            {/* )} */}
          </div>
          <Dialog
            open={approvalDialogOpen}
            onClose={closeApprovalDialog}
            aria-labelledby="approve-beneficiary-dialog-title"
            aria-describedby="approve-beneficiary-dialog-description"
          >
            <DialogTitle id="approve-beneficiary-dialog-title">Approve Beneficiary</DialogTitle>
            <DialogContent>
              <DialogContentText id="approve-beneficiary-dialog-description">
                Are you sure you want to approve this beneficiary?
                <br /><br />
                Please note this will create/update a beneficiary.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeApprovalDialog}>Cancel</Button>
              <Button onClick={confirmApproval} color="primary" variant="contained" autoFocus>
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </AdminCommonFormLayout>
  )
}